import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

/**
 * The four logical filter tabs surfaced above the list. They translate to
 * Hub query params as follows:
 *
 *   all         -> no params (server default: four-zone ordering — cuci,
 *                  pinned, unread, read; Special has NO zone of its own)
 *   unread_pin  -> view=unread_pin (only pinned OR unread rows; classic
 *                  pinned-first-then-recency ordering)
 *   cuci        -> view=cuci (only rows tagged 要cuci; classic ordering)
 *   special     -> view=special (only rows tagged Special; classic ordering)
 *
 * These replace the old status/assigned tabs (Pending / Mine / Unclaimed /
 * Closed) — the claim/assign feature is deprecated, so the filters are now
 * driven entirely by the GLOBAL operator flags (`pinned_at` /
 * `marked_unread` / `tags`, see `conversations.api.ts`). "Unread" always
 * means `unread_count > 0 OR marked_unread`.
 */
export type ConversationFilter = 'all' | 'unread_pin' | 'cuci' | 'special';

const TABS: { value: ConversationFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'unread_pin', label: 'Unread & Pin' },
  { value: 'cuci', label: 'Cuci' },
  { value: 'special', label: 'Special' },
];

interface ConversationFilterTabsProps {
  value: ConversationFilter;
  onValueChange: (next: ConversationFilter) => void;
}

export function ConversationFilterTabs({
  value,
  onValueChange,
}: ConversationFilterTabsProps): React.JSX.Element {
  return (
    <Tabs
      value={value}
      onValueChange={(next) => onValueChange(next as ConversationFilter)}
    >
      <TabsList className="grid h-9 w-full grid-cols-4 gap-1 p-[5px]">
        {TABS.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            // overflow-hidden + a truncating INNER span: the list panel is
            // user-resizable and 'Unread & Pin' at 11px overflows its grid
            // cell below ~300px. The trigger is inline-flex, so 'truncate'
            // on the trigger itself would clip the text child WITHOUT an
            // ellipsis — the span is the block-level text box that can
            // actually ellipsize.
            className="h-7 overflow-hidden px-1 text-[11px]"
          >
            <span className="truncate">{tab.label}</span>
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  );
}
